/**
 * Run `worker` over `items`, never more than `limit` at once.
 *
 * Runners share a cursor rather than being handed a slice, so one slow file
 * doesn't idle a slot while others wait.
 *
 * Shared by the two batch upload paths — the job files tab and the expense
 * receipt flow — because both need the same ceiling for the same reason: an
 * unbounded fan-out pushes every decode onto the server at once, and the API
 * runs on a 1GB VM.
 */
export async function pool<T>(
  items: T[],
  limit: number,
  worker: (item: T) => Promise<void>,
): Promise<void> {
  let cursor = 0;
  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    for (let i = cursor++; i < items.length; i = cursor++) {
      const item = items[i];
      if (item !== undefined) await worker(item);
    }
  });
  await Promise.all(runners);
}

/**
 * How many uploads run at once.
 *
 * Three, not twenty: a file the browser couldn't decode is decoded server-side
 * instead, and twenty concurrent libheif decodes would exhaust the API's memory.
 */
export const UPLOAD_CONCURRENCY = 3;
